import { Button } from '@components/core-ui/form-elements/buttons/Button/';
import { OutlinedButton } from '@components/core-ui/form-elements/buttons/Button/variations/OutlinedButton';
import { Input } from '@components/core-ui/form-elements/Input';
import { AbsoluteSpinner } from '@components/core-ui/spinners/Spinner';
import { ModalTitle } from '@modals/core-ui/ModalTitle';
import { ModalWrapper } from '@modals/core-ui/ModalWrapper';
import { useModalContext } from '@providers/ModalProvider';
import { useAPI } from '@providers/useAPI';
import { SubscriptionsService } from 'app/API';
import { ModalNamesEnum } from 'app/types/enums/ModalNamesEnum';
import React, { useState } from 'react';
import { Content } from './styles/Content';
import { RangeSliderWrapper } from './styles/RangeSliderWrapper';
import { TProps } from './types/TProps';

export const AdminConfirmSubscription = ({
  billingAccountId,
  subscriptionProductId,
  planName,
  price,
  freeSeats,
  onCancel,
  onSuccess,
}: TProps) => {
  const { call } = useAPI();
  const { openModal, closeModal } = useModalContext();
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [numberOfLicenses, setNumberOfLicenses] = useState(1);

  const handleError = (message?: string, result?: { error?: string }) => {
    let errorMessage = message ?? result?.error ?? 'Something went wrong. Please contact administrator.';
    if (result?.error === 'No payment methods found') {
      errorMessage = 'Cannot create subscription. Please add a payment method first.';
    }
    if (result?.error === 'Subscription already exists') {
      errorMessage = 'You already have this subscription. Please choose another or modify an existing subscription.';
    }
    onCancel();
    openModal(ModalNamesEnum.GenericErrorModal, {
      message: errorMessage,
    });
  };

  const innerSubmit = async () => {
    setIsSubmitting(true);
    let requestErrorMessage: string | undefined;
    try {
      const result = await call(
        () =>
          SubscriptionsService.adminCreate({
            baId: billingAccountId,
            requestBody: { subscriptionProductId: subscriptionProductId, licenseCount: numberOfLicenses },
          }),
        {
          onError: (message) => {
            requestErrorMessage = message;
          },
        },
      );
      if (!result || !result.success) {
        handleError(undefined, result);
        return;
      }
      closeModal();
      onSuccess();
    } catch {
      handleError(requestErrorMessage);
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <ModalWrapper width="480px">
      {isSubmitting ? <AbsoluteSpinner overlay={true} /> : null}
      <ModalTitle data-testid="purchasePlan">Confirm</ModalTitle>
      <Content>
        <p className="description">
          You are about to purchase the &quot;{planName}&quot; plan for ${price.toFixed(2)} per license per month +
          applicable sales tax
        </p>

        <RangeSliderWrapper>
          <label htmlFor="steps-range" className="block mb-2 font-[600] text-xs text-foreground-muted text-left">
            Number of licenses:
          </label>
          <Input
            onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
              setNumberOfLicenses(parseInt(e.target.value, 10) || 1)
            }
            label="Number of licenses"
            value={numberOfLicenses}
          />
          <span className="text-xs">
            {numberOfLicenses} license{numberOfLicenses > 1 && 's'}
          </span>
        </RangeSliderWrapper>

        <div className="text-left my-4 flex flex-col">
          {freeSeats ? (
            <div>
              <span className="font-[600] text-xs text-foreground-muted">Free licenses in subscription plan:</span>
              <span className="float-right">{freeSeats}</span>
            </div>
          ) : null}
          <div>
            <span className="font-[600] text-xs text-foreground-muted">Total subscription price per month:</span>
            <span className="float-right">
              ${numberOfLicenses <= freeSeats ? 0 : (Number(price) * (numberOfLicenses - freeSeats)).toFixed(2)}
            </span>
          </div>
        </div>

        <div className="buttonContainer">
          <OutlinedButton className="w-full" type="button" onClick={onCancel}>
            Cancel
          </OutlinedButton>
          <Button
            className="w-full"
            data-testid="submitButton"
            type="submit"
            onClick={() => innerSubmit()}
            disabled={isSubmitting}
          >
            Purchase
          </Button>
        </div>
      </Content>
    </ModalWrapper>
  );
};
